import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";

const HOTMART_BASE = "https://pay.hotmart.com/N106369191W?off=gisrp2lr";
const TRACK_SCRIPT_BASE = "https://kegqh.ttrk.io/track.js";
const TRACK_CMP_ID = "6a321a03014cc93d3a537df0";

const leadSchema = z.object({
  name: z.string().trim().min(2, "Nome deve ter pelo menos 2 caracteres").max(100),
  email: z.string().trim().toLowerCase().email("E-mail inválido").max(255),
  phone: z.string().trim().refine((val) => {
    const digits = val.replace(/\D/g, "");
    return digits.length >= 8 && digits.length <= 15;
  }, "Número de WhatsApp inválido"),
});

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
function setCookie(name: string, value: string, maxAgeSec: number) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
}
function generateEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function getOrCreateExternalId(): string {
  if (typeof window === "undefined") return "";
  const KEY = "lvbl_eid";
  let id = "";
  try { id = window.localStorage.getItem(KEY) || ""; } catch { /* ignore */ }
  if (!id) id = getCookie(KEY) || "";
  if (!id) {
    id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  try { window.localStorage.setItem(KEY, id); } catch { /* ignore */ }
  setCookie(KEY, id, 60 * 60 * 24 * 365);
  return id;
}

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

// Strip leading +XX country code and split into ddd + local number (BR-friendly)
function splitPhone(phone: string): { ddd: string; number: string } {
  const digits = phone.replace(/\D/g, "");
  let local = digits;
  if (digits.startsWith("55") && digits.length >= 12) {
    local = digits.slice(2);
  }
  const ddd = local.slice(0, 2);
  const number = local.slice(2);
  return { ddd, number };
}

export const Route = createFileRoute("/aplicacao")({
  head: () => ({
    meta: [
      { title: "Aplicação — Bootcamp Cura pela Alimentação" },
      {
        name: "description",
        content:
          "Preencha sua aplicação para garantir sua vaga no Bootcamp Cura pela Alimentação.",
      },
      { property: "og:title", content: "Aplicação — Bootcamp Cura pela Alimentação" },
      {
        property: "og:description",
        content: "Garanta sua vaga no Bootcamp Cura pela Alimentação.",
      },
    ],
  }),
  component: Aplicacao,
});

function Aplicacao() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // tracking bootstrap: track.js + capture clickid + AplicacaoBootcamp postback
  useEffect(() => {
    if (typeof window === "undefined") return;

    void import("@/lib/visitor-identity").then((m) => m.resolveFbClick());
    getOrCreateExternalId();

    // load track.js
    const existing = document.querySelector('script[src*="kegqh.ttrk.io/track.js"]');
    if (!existing) {
      const s = document.createElement("script");
      s.src = `${TRACK_SCRIPT_BASE}?rtkcmpid=${TRACK_CMP_ID}&t=${Date.now()}`;
      s.async = true;
      document.head.appendChild(s);
    }

    // capture clickid from URL
    const fromUrl = new URLSearchParams(window.location.search).get("clickid");
    if (fromUrl) {
      try { window.localStorage.setItem("clickid", fromUrl); } catch { /* ignore */ }
    }

  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const result = leadSchema.safeParse({ name, email, phone });
    if (!result.success) {
      toast.error(result.error.errors[0].message);
      return;
    }
    setSubmitting(true);
    try {
      const data = result.data;
      const { ddd, number } = splitPhone(phone);

      // clickid
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("clickid") || params.get("click_id") || params.get("xcod");
      const clickid =
        fromUrl ||
        window.localStorage.getItem("clickid") ||
        window.sessionStorage.getItem("clickid") ||
        "";
      const clickidSource = fromUrl
        ? "url"
        : window.localStorage.getItem("clickid")
          ? "localStorage"
          : "none";

      const eventId = generateEventId();
      const fbp = getCookie("_fbp");
      const { saveVisitorIdentity, resolveFbClick, applyAdvancedMatching, splitFullName } =
        await import("@/lib/visitor-identity");
      const fbClick = resolveFbClick();
      const fbc = fbClick.fbc;
      const externalId = getOrCreateExternalId();
      const { firstName, lastName } = splitFullName(data.name);
      saveVisitorIdentity({ email: data.email, phone, firstName, lastName });
      applyAdvancedMatching();

      // Meta Pixel InitiateCheckout com eventID p/ dedup
      try {
        if (typeof window.fbq === "function") {
          window.fbq(
            "track",
            "InitiateCheckout",
            {
              content_name: "Aplicacao Saude Maxima",
              content_category: "Bootcamp",
            },
            { eventID: eventId },
          );
        }
      } catch { /* ignore */ }

      // InitiateCheckout postback p/ RedTrack
      if (clickid) {
        fetch(
          `https://kegqh.ttrk.io/postback?type=InitiateCheckout&clickid=${encodeURIComponent(clickid)}`,
        ).catch(() => {});
      }

      // Salva no backend (keepalive p/ não atrasar o redirect)
      fetch("/api/public/aplicacao", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone,
          phone_ddd: ddd,
          phone_number: number,
          clickid: clickid || null,
          clickid_source: clickidSource !== "none" ? clickidSource : null,
          meta_event_id: eventId,
          fbp,
          fbc,
          external_id: externalId,
          fbclid: fbClick.fbclid,
        }),
        keepalive: true,
      }).catch(() => {});

      toast.success("Redirecionando para o checkout...");

      // Hotmart com parâmetros personalizados
      const sckParam = clickid ? `&sck=${encodeURIComponent(clickid)}` : "";
      const url =
        `${HOTMART_BASE}` +
        `&name=${encodeURIComponent(data.name)}` +
        `&email=${encodeURIComponent(data.email)}` +
        `&phoneac=${encodeURIComponent(ddd)}` +
        `&phonenumber=${encodeURIComponent(number)}` +
        sckParam;

      setTimeout(() => {
        window.location.href = url;
      }, 600);
    } catch (err) {
      console.error(err);
      toast.error("Ocorreu um erro. Tente novamente.");
      setSubmitting(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-start justify-center px-4 py-8 sm:items-center sm:py-12">
      {/* Anchor invisível para track.js gerar clickid */}
      <a
        href="https://kegqh.ttrk.io/click"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      >
        <button type="button" tabIndex={-1}>Track</button>
      </a>

      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-leaf/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-leaf-bright/10 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-leaf/40 bg-leaf/10 px-4 py-1.5">
            <Sparkles className="size-4 text-leaf-bright" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-cream">
              Aplicação
            </span>
          </div>
          <h1 className="font-display text-2xl font-bold uppercase leading-tight text-cream sm:text-3xl">
            <span className="text-leaf-bright">APLICAÇÃO SAÚDE MÁXIMA</span>
          </h1>
          <p className="mt-3 text-sm text-cream/70">
            Preencha as informações abaixo para continuar.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-5 rounded-3xl border border-leaf/20 bg-card/80 p-6 backdrop-blur-sm sm:p-8"
          style={{ boxShadow: "var(--shadow-card)" }}
          noValidate
        >
          <div>
            <label htmlFor="name" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-cream/80">
              Nome completo
            </label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome completo"
              className="w-full rounded-xl border border-leaf/30 bg-deep/60 px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-leaf-bright focus:outline-none focus:ring-2 focus:ring-leaf-bright/40"
            />
          </div>

          <div>
            <label htmlFor="email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-cream/80">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="w-full rounded-xl border border-leaf/30 bg-deep/60 px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-leaf-bright focus:outline-none focus:ring-2 focus:ring-leaf-bright/40"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-cream/80">
              WhatsApp
            </label>
            <PhoneInput value={phone} onChange={setPhone} />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-2xl bg-leaf-bright py-4 text-base font-bold uppercase tracking-tight text-deep transition-all hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
            style={{ boxShadow: "0 10px 30px -5px oklch(0.72 0.2 140 / 0.4)" }}
          >
            {submitting ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="size-5 animate-spin" /> Processando…
              </span>
            ) : (
              "DESBLOQUEAR MEU ACESSO →"
            )}
          </button>

          <p className="text-center text-[10px] tracking-tight text-cream/40">
            Ao continuar, você será redirecionado para o checkout seguro.
          </p>
        </form>
      </div>
    </main>
  );
}
